import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Building2, ImageUp, Loader2, Type, X } from 'lucide-react';
import { validateCxEmailLogoFile, CX_EMAIL_LOGO_ACCEPT } from '../utils/cxEmailLogoUpload';
import CxExpAnchorTooltip from './CxExpAnchorTooltip';

function authHeaders() {
  const token = localStorage.getItem('authToken');
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

function revokeBlobPreviewUrl(url) {
  if (url && String(url).startsWith('blob:')) {
    URL.revokeObjectURL(url);
  }
}

export default function CxEmailCompanyImages({ active, onCompaniesChange, reloadKey = 0 }) {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploadingCodigo, setUploadingCodigo] = useState(null);
  const [deletingCodigo, setDeletingCodigo] = useState(null);
  const [rowError, setRowError] = useState('');
  const [previewUrls, setPreviewUrls] = useState({});
  const fileRefs = useRef({});

  const loadCompanies = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/vc-usr/cx/config/images/empresas', { headers: authHeaders() });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json.success === false) throw new Error(json.message || `Erro ${res.status}`);
      const list = json.data?.companies || [];
      setCompanies(list);
      onCompaniesChange?.(list);
      const previews = {};
      list.forEach((c) => {
        if (c.image?.viewUrl) {
          previews[c.codigo] = c.image.viewUrl;
        }
      });
      setPreviewUrls((prev) => {
        Object.values(prev).forEach(revokeBlobPreviewUrl);
        return previews;
      });
    } catch (e) {
      setError(e.message || 'Erro ao carregar empresas');
    } finally {
      setLoading(false);
    }
  }, [onCompaniesChange]);

  useEffect(() => {
    if (active) loadCompanies();
  }, [active, loadCompanies, reloadKey]);

  useEffect(() => () => {
    Object.values(previewUrls).forEach(revokeBlobPreviewUrl);
  }, [previewUrls]);

  const handleFileSelect = async (codigo, event) => {
    const input = event.currentTarget;
    const file = input?.files?.[0];
    if (!file || !(file instanceof Blob)) return;
    if (input.files.length > 1) {
      input.value = '';
      setRowError('Envie apenas uma imagem por vez.');
      return;
    }

    const previousPreview = previewUrls[codigo];
    setUploadingCodigo(codigo);
    setRowError('');

    let immediatePreview = null;
    try {
      const uploadFile = validateCxEmailLogoFile(file);
      immediatePreview = URL.createObjectURL(uploadFile);
      setPreviewUrls((prev) => ({ ...prev, [codigo]: immediatePreview }));
      setCompanies((prev) => {
        const next = prev.map((c) =>
          c.codigo === codigo
            ? { ...c, image: { ...(c.image || {}), viewUrl: immediatePreview } }
            : c
        );
        onCompaniesChange?.(next);
        return next;
      });

      const presignRes = await fetch(
        `/api/vc-usr/cx/config/images/empresas/${encodeURIComponent(codigo)}/presign`,
        {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({
            mimeType: uploadFile.type,
            sizeBytes: uploadFile.size,
            fileName: uploadFile.name,
          }),
        }
      );
      const presignData = await presignRes.json();
      if (!presignRes.ok || !presignData.success) {
        throw new Error(presignData.message || 'Falha ao preparar upload');
      }

      const uploadRes = await fetch(presignData.data.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': uploadFile.type },
        body: uploadFile,
      });
      if (!uploadRes.ok) throw new Error('Falha ao enviar imagem para o servidor');

      const completeRes = await fetch(
        `/api/vc-usr/cx/config/images/empresas/${encodeURIComponent(codigo)}/complete`,
        {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({ s3Key: presignData.data.key }),
        }
      );
      const completeData = await completeRes.json();
      if (!completeRes.ok || !completeData.success) {
        throw new Error(completeData.message || 'Falha ao confirmar upload');
      }

      revokeBlobPreviewUrl(immediatePreview);
      const viewUrl = completeData.data?.image?.viewUrl || null;
      setPreviewUrls((prev) => ({ ...prev, [codigo]: viewUrl }));
      setCompanies((prev) => {
        const next = prev.map((c) =>
          c.codigo === codigo ? { ...c, image: completeData.data?.image || c.image } : c
        );
        onCompaniesChange?.(next);
        return next;
      });
    } catch (err) {
      if (immediatePreview) revokeBlobPreviewUrl(immediatePreview);
      setPreviewUrls((prev) => ({ ...prev, [codigo]: previousPreview }));
      setRowError(err.message || 'Erro ao enviar logomarca');
    } finally {
      setUploadingCodigo(null);
      input.value = '';
    }
  };

  const handleDeleteImage = async (codigo) => {
    setDeletingCodigo(codigo);
    setRowError('');
    const previousPreview = previewUrls[codigo];
    try {
      const res = await fetch(
        `/api/vc-usr/cx/config/images/empresas/${encodeURIComponent(codigo)}`,
        { method: 'DELETE', headers: authHeaders() }
      );
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json.success === false) {
        throw new Error(json.message || 'Falha ao excluir logomarca');
      }
      revokeBlobPreviewUrl(previousPreview);
      setPreviewUrls((prev) => {
        const next = { ...prev };
        delete next[codigo];
        return next;
      });
      setCompanies((prev) => {
        const next = prev.map((c) => (c.codigo === codigo ? { ...c, image: null } : c));
        onCompaniesChange?.(next);
        return next;
      });
    } catch (err) {
      setRowError(err.message || 'Erro ao excluir logomarca');
    } finally {
      setDeletingCodigo(null);
    }
  };

  if (loading) {
    return (
      <div className="cx-email-images-loading">
        <Loader2 size={16} className="cx-exp-spin" aria-hidden />
        Carregando empresas...
      </div>
    );
  }

  if (error) {
    return (
      <div className="cx-email-images-error">
        <p>{error}</p>
        <button type="button" className="cx-email-btn cx-email-btn--ghost" onClick={loadCompanies}>
          Tentar novamente
        </button>
      </div>
    );
  }

  if (!companies.length) {
    return <p className="cx-email-field-hint">Nenhuma empresa ativa encontrada.</p>;
  }

  return (
    <div className="cx-email-config-row cx-email-config-row--last">
      <div className="cx-email-config-row-head">
        <Building2 size={14} aria-hidden />
        <h3>Empresas</h3>
      </div>
      <div className="cx-email-images-section-intro">
        <p className="cx-email-field-hint cx-email-images-section-hint">
          Com logomarca, o cabeçalho exibe a imagem; sem logomarca, exibe o nome da empresa.
        </p>
        <p className="cx-email-images-format-hint">
          <span>PNG, JPG ou WebP</span>
          <span className="cx-email-images-format-sep" aria-hidden="true">·</span>
          <span>Máximo 10 MB</span>
        </p>
      </div>
      <ul className="cx-email-empresa-list">
        {companies.map((emp) => {
          const preview = previewUrls[emp.codigo] || null;
          const hasLogo = Boolean(preview);
          const isBusy = uploadingCodigo === emp.codigo || deletingCodigo === emp.codigo;
          return (
            <li key={emp.codigo} className="cx-email-empresa-row">
              <div className="cx-email-empresa-info">
                <span className="cx-email-empresa-codigo">{emp.codigo}</span>
                <div className="cx-email-empresa-text">
                  <span className="cx-email-empresa-nome" title={emp.nome}>
                    {emp.nome}
                  </span>
                  {emp.cnpj ? (
                    <span className="cx-email-empresa-cnpj">{emp.cnpj}</span>
                  ) : null}
                </div>
              </div>
              <div className="cx-email-empresa-logo-row">
                <div
                  className={`cx-email-empresa-logo-preview${hasLogo ? '' : ' cx-email-empresa-logo-preview--text'}`}
                  aria-hidden={hasLogo ? undefined : true}
                >
                  {hasLogo ? (
                    <img src={preview} alt="" className="cx-email-empresa-logo-preview-img" />
                  ) : (
                    <Type size={16} strokeWidth={2} aria-hidden="true" />
                  )}
                </div>
                <div className="cx-email-empresa-logo-actions">
                  <input
                    ref={(el) => {
                      fileRefs.current[emp.codigo] = el;
                    }}
                    type="file"
                    accept={CX_EMAIL_LOGO_ACCEPT}
                    className="cx-email-empresa-logo-input-hidden"
                    onChange={(e) => handleFileSelect(emp.codigo, e)}
                  />
                  <CxExpAnchorTooltip text="Atualizar logo" align="end" compact>
                    <button
                      type="button"
                      className="cx-email-icon-btn"
                      disabled={isBusy}
                      aria-label="Atualizar logo"
                      onClick={() => fileRefs.current[emp.codigo]?.click()}
                    >
                      {uploadingCodigo === emp.codigo ? (
                        <Loader2 size={14} className="cx-exp-spin" aria-hidden />
                      ) : (
                        <ImageUp size={14} strokeWidth={2} aria-hidden />
                      )}
                    </button>
                  </CxExpAnchorTooltip>
                  {hasLogo ? (
                    <CxExpAnchorTooltip text="Excluir imagem" align="end" compact>
                      <button
                        type="button"
                        className="cx-email-icon-btn cx-email-icon-btn--danger"
                        disabled={isBusy}
                        aria-label="Excluir imagem"
                        onClick={() => handleDeleteImage(emp.codigo)}
                      >
                        {deletingCodigo === emp.codigo ? (
                          <Loader2 size={14} className="cx-exp-spin" aria-hidden />
                        ) : (
                          <X size={14} strokeWidth={2} aria-hidden />
                        )}
                      </button>
                    </CxExpAnchorTooltip>
                  ) : null}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
      {rowError ? <p className="cx-email-error">{rowError}</p> : null}
    </div>
  );
}
